import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { apiAgent } from "../../../axiosConfig";
import { Button, IconButton } from "../../../design_system/components/Button/Button";
import { Popup } from "../../../design_system/components/Popup/Popup.jsx";
import { InputBox } from "../../../design_system/components/Input Fields/InputBox.jsx";
import { InputArea } from "../../../design_system/components/Input Fields/InputArea.jsx";
import { SelectBox } from "../../../design_system/components/Input Fields/SelectBox.jsx";
import { Loader } from "../../../design_system/components/Loader";
import { SelectionControl } from "../../../design_system/components/SelectionControls/SelectionControl.jsx";
import { ClickableDropdown } from "../../../design_system/components/Dropdown/Dropdown";
import { DropdownMenu } from "../../../design_system/components/Dropdown/DropdownMenu";
import { DropdownItem } from "../../../design_system/components/Dropdown/DropdownItem";
import { Icons } from "../../../design_system/components/Icons";
import { Popover } from "../../../design_system/components/ToolTip/ToolTip.jsx";
import { EmptyState } from "../../../design_system/components/EmptyState/EmptyState.jsx";
import { AnConfirmDropdown } from "../../cta_utils/AnConfirmDropdown";
import ConditionalRender from "../../../ConditionalRender";
import useCheckUserAccess from "../../custom_hooks/useCheckUserAccess";
import { useToast } from "../../../design_system/components/Toast/ToastContext.jsx";
import getApiUrl from "../../utils/UrlHelper";
import { isCustomerTeamAdmin, usesPolicyAuthorization } from "../../../utils/access.js";

const DEFAULT_FIELDS = [
    "Name", "Company Name", "Full Name", "Spouse Name", "College Name",
    "Course", "Branch", "Graduation Year", "Past Education", "City",
    "State", "Country", "Location", "Title/Position", "LinkedIn URL",
    "Alias Names", "Other Companies",
];

const CHOICE_TYPES = ["Radio Button", "Check Box", "Select Box"];
const FIELD_TYPES = ["Text Box", "Text Area", ...CHOICE_TYPES];
const FIELD_NAME_LIMIT = 40;
const DUPLICATE_NAME_ERROR = "A custom field with this name already exists.";
const CONFIRM_WIDTH = 260;

const anchorPosition = (element) => {
    const rect = element.getBoundingClientRect();
    return { top: rect.bottom + 8, left: rect.right - CONFIRM_WIDTH };
};
const CONFIRM_Z_INDEX = 1210;

const FIELD_TYPE_HELP = {
    "Text Box": "A single line of free text — no setup needed.",
    "Text Area": "Long free text — notes, summaries, or context.",
    "Radio Button": "A prospect picks exactly one option.",
    "Select Box": "A prospect picks one option from a dropdown.",
    "Check Box": "A prospect can pick one or more options.",
};

const FIELD_TYPE_PICKER_HELP = {
    "Text Box": "Short free text — name, ID, link.",
    "Text Area": "Long free text — notes, summary.",
    "Radio Button": "Pick exactly one from a fixed list.",
    "Check Box": "Pick one or more from a fixed list.",
    "Select Box": "Pick one from a long list, in a dropdown.",
};

const CHOICE_MARKER_TYPES = {
    "Radio Button": "radio",
    "Check Box": "checkbox",
    "Select Box": "select",
};

const ChoiceMarker = ({ markerType }) => (
    <span className={`data-fields__choice-marker data-fields__choice-marker--${markerType}`}>
        {markerType === "select" && <Icons name="chevron-down" size={12} />}
    </span>
);

const renderFieldTypeGlyph = (fieldType) => (
    CHOICE_MARKER_TYPES[fieldType]
        ? <ChoiceMarker markerType={CHOICE_MARKER_TYPES[fieldType]} />
        : <span className={`data-fields__type-glyph data-fields__type-glyph--${fieldType === "Text Area" ? "area" : "box"}`} />
);

const emptyOption = () => ({ id: null, label: "" });
const emptyForm = () => ({ field_name: "", field_type: "", choice_options: [] });

const normalizeOptions = (field) => {
    if (field.choice_options?.length) {
        return field.choice_options.map(({ id, label }) => ({ id, label }));
    }
    return (field.options || []).map((label) => ({ id: null, label }));
};

const formSnapshot = (form) => JSON.stringify({
    field_name: form.field_name.trim(),
    field_type: form.field_type,
    choice_options: form.choice_options.map(({ id, label }) => ({ id, label: label.trim() })),
});

const errorMessage = (error, fallback) => (
    error.response?.data?.error ||
    error.response?.data?.error_message ||
    error.response?.data?.message ||
    fallback
);

const ManageDataFields = (props) => {
    const [customFields, setCustomFields] = useState([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState({});
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [initialSnapshot, setInitialSnapshot] = useState("");
    const [selectedField, setSelectedField] = useState(null);
    const [validationShown, setValidationShown] = useState(false);
    const [serverFieldError, setServerFieldError] = useState(null);
    const [optionCounts, setOptionCounts] = useState({});
    const [countsByField, setCountsByField] = useState({});
    const [bulkOptions, setBulkOptions] = useState("");
    const [removal, setRemoval] = useState({ option: null, answer_action: "clear", replacement_option_id: "" });
    const [deleteConfirmation, setDeleteConfirmation] = useState("");
    const [draggedOptionIndex, setDraggedOptionIndex] = useState(null);
    const [confirmAnchor, setConfirmAnchor] = useState(null);
    const [confirmAnchorEl, setConfirmAnchorEl] = useState(null);

    const selectedFieldRef = useRef(null);
    selectedFieldRef.current = selectedField;

    const currentTeam = useSelector((state) => state.current_team.team);
    const checkUserAccess = useCheckUserAccess();
    const toast = useToast();
    const history = useHistory();

    const choiceField = CHOICE_TYPES.includes(form.field_type);
    const policyAccessEnabled = usesPolicyAuthorization(currentTeam);
    const customerTeamAdmin = isCustomerTeamAdmin({ team: currentTeam });
    const canCreateCustomField = policyAccessEnabled
        ? checkUserAccess("custom_filed_adding")
        : customerTeamAdmin;
    const canEditCustomField = policyAccessEnabled
        ? checkUserAccess("custom_fields.update")
        : customerTeamAdmin;
    const canDeleteCustomField = policyAccessEnabled
        ? checkUserAccess("custom_fields.delete")
        : customerTeamAdmin;
    const hasFilterAccess = checkUserAccess("enable_custom_field_filter") && canEditCustomField;
    const isDirty = Boolean(initialSnapshot) && formSnapshot(form) !== initialSnapshot;
    const isFieldEditorOpen = modal === "fieldForm" || modal === "removeOption" || modal === "discardChanges";
    const optionsChanged = !selectedField || !initialSnapshot ||
        JSON.stringify(JSON.parse(initialSnapshot).choice_options) !== JSON.stringify(JSON.parse(formSnapshot(form)).choice_options);
    const allDefaultFields = currentTeam.save_to_crm_enabled
        ? ["CRM ID", ...DEFAULT_FIELDS]
        : DEFAULT_FIELDS;

    const validation = useMemo(() => {
        const result = { options: {} };
        const fieldName = form.field_name.trim();
        const duplicate = customFields.some((field) => (
            field.id !== selectedField?.id &&
            field.field_name.trim().toLowerCase() === fieldName.toLowerCase()
        ));

        if (!fieldName) result.field_name = "Field name is required.";
        else if (fieldName.length > FIELD_NAME_LIMIT) result.field_name = `Field name cannot exceed ${FIELD_NAME_LIMIT} characters.`;
        else if (duplicate) result.field_name = DUPLICATE_NAME_ERROR;

        if (choiceField && optionsChanged) {
            const labels = form.choice_options.map((option) => option.label.trim());
            const seen = new Map();

            if (labels.length < 2) result.summary = "Add at least two options.";
            labels.forEach((label, index) => {
                if (!label) {
                    result.options[index] = "Option can’t be blank. Add a label or remove the row.";
                    return;
                }
                const normalized = label.toLowerCase();
                if (seen.has(normalized)) {
                    result.options[index] = "Already added — option labels must be unique.";
                    result.options[seen.get(normalized)] = "Already added — option labels must be unique.";
                } else {
                    seen.set(normalized, index);
                }
                if (form.field_type === "Check Box" && label.includes(", ")) {
                    result.options[index] = "Checkbox options cannot contain a comma followed by a space.";
                }
            });
        }

        return result;
    }, [form, choiceField, optionsChanged, customFields, selectedField]);

    // A new editor starts empty; only show this error after submit. Existing
    // fields should show it when their name is cleared while editing.
    const nameClearedByUser = Boolean(initialSnapshot) && Boolean(selectedField) && !form.field_name.trim();
    const fieldNameError = serverFieldError ||
        ((validationShown || nameClearedByUser) ? validation.field_name : null);
    const optionErrors = validationShown ? validation.options : {};
    const optionSummaryError = validationShown ? validation.summary : null;

    const bulkSummary = useMemo(() => {
        const existing = new Set(form.choice_options.map((option) => option.label.trim().toLowerCase()).filter(Boolean));
        const additions = [];
        const duplicates = [];

        bulkOptions.split("\n").map((label) => label.trim()).filter(Boolean).forEach((label) => {
            const normalized = label.toLowerCase();
            if (existing.has(normalized)) {
                duplicates.push(label);
                return;
            }
            existing.add(normalized);
            additions.push(label);
        });

        return { additions, duplicates };
    }, [bulkOptions, form.choice_options]);

    useEffect(() => {
        props.setActiveSettingTab("data_fields");
        apiAgent({ method: "get", url: "/team/custom_fields", params: { with_counts: true } })
            .then((response) => setCustomFields(response.data.custom_fields || []))
            .catch(() => toast.error("Failed to load custom fields"))
            .finally(() => setIsDataLoaded(true));
        // This settings tab loads once and the parent setter is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (modal !== "toggleFilter" || !confirmAnchorEl) return undefined;

        const reposition = () => setConfirmAnchor(anchorPosition(confirmAnchorEl));
        reposition();
        window.addEventListener("scroll", reposition, true);
        window.addEventListener("resize", reposition);
        return () => {
            window.removeEventListener("scroll", reposition, true);
            window.removeEventListener("resize", reposition);
        };
    }, [modal, confirmAnchorEl]);

    const handleNavigate = (path) => {
        props.onClose?.();
        history.push(path);
    };

    const resetEditor = () => {
        setModal(null);
        setForm(emptyForm());
        setInitialSnapshot("");
        setSelectedField(null);
        setValidationShown(false);
        setServerFieldError(null);
        setOptionCounts({});
        setBulkOptions("");
        setRemoval({ option: null, answer_action: "clear", replacement_option_id: "" });
        setDraggedOptionIndex(null);
        setConfirmAnchor(null);
        setConfirmAnchorEl(null);
    };

    const requestEditorClose = () => {
        if (isDirty) {
            setModal("discardChanges");
            return;
        }
        resetEditor();
    };

    const openCreate = () => {
        const nextForm = emptyForm();
        setSelectedField(null);
        setForm(nextForm);
        setInitialSnapshot(formSnapshot(nextForm));
        setValidationShown(false);
        setServerFieldError(null);
        setOptionCounts({});
        setModal("fieldForm");
    };

    const forgetCounts = (fieldId) => setCountsByField((state) => {
        const next = { ...state };
        delete next[fieldId];
        return next;
    });

    const loadOptionCounts = (field, setAsActive = false) => {
        if (countsByField[field.id] || isLoading[`counts-${field.id}`]) return;

        setIsLoading((state) => ({ ...state, [`counts-${field.id}`]: true }));
        apiAgent({ method: "get", url: `/team/custom_fields/${field.id}/option_counts`, params: {} })
            .then(({ data }) => {
                const counts = data.option_counts || {};
                setCountsByField((state) => ({ ...state, [field.id]: counts }));
                if (setAsActive) setOptionCounts((current) => (selectedFieldRef.current?.id === field.id ? counts : current));
            })
            .catch(() => toast.error("Failed to load option counts"))
            .finally(() => setIsLoading((state) => ({ ...state, [`counts-${field.id}`]: false })));
    };

    const openEdit = (field) => {
        const nextForm = {
            field_name: field.field_name,
            field_type: field.field_type,
            choice_options: normalizeOptions(field),
        };
        setSelectedField(field);
        setForm(nextForm);
        setInitialSnapshot(formSnapshot(nextForm));
        setValidationShown(false);
        setServerFieldError(null);
        setOptionCounts(countsByField[field.id] || {});
        setModal("fieldForm");
        loadOptionCounts(field, true);
    };

    const setFieldType = (fieldType) => {
        setForm((state) => ({
            ...state,
            field_type: fieldType,
            choice_options: CHOICE_TYPES.includes(fieldType)
                ? (state.choice_options.length >= 2 ? state.choice_options : [emptyOption(), emptyOption()])
                : [],
        }));
        setValidationShown(false);
        setServerFieldError(null);
    };

    const updateOption = (index, label) => {
        setForm((state) => ({
            ...state,
            choice_options: state.choice_options.map((option, optionIndex) => (
                optionIndex === index ? { ...option, label } : option
            )),
        }));
    };

    const addOption = () => {
        setForm((state) => ({ ...state, choice_options: [...state.choice_options, emptyOption()] }));
    };

    const moveOption = (source, destination) => {
        if (!Number.isInteger(source) || !Number.isInteger(destination)) return;
        if (source === destination || source < 0 || destination < 0 || destination >= form.choice_options.length) return;
        setForm((state) => {
            const options = [...state.choice_options];
            const [moved] = options.splice(source, 1);
            options.splice(destination, 0, moved);
            return { ...state, choice_options: options };
        });
    };

    const validateForm = () => {
        setValidationShown(true);
        return !validation.field_name && !validation.summary && Object.keys(validation.options).length === 0;
    };

    const saveField = () => {
        if (!validateForm()) {
            setTimeout(() => {
                document.querySelector(".data-fields__option-note--error, .data-fields__field-helper.color-text-error")
                    ?.scrollIntoView({ block: "center" });
            }, 0);
            return;
        }

        setIsLoading((state) => ({ ...state, saveField: true }));
        const payload = {
            field_id: selectedField?.id,
            field_name: form.field_name.trim(),
            field_type: form.field_type,
            choice_options: choiceField && optionsChanged
                ? form.choice_options.map(({ id, label }) => ({ id, label: label.trim() }))
                : undefined,
        };

        apiAgent({ method: "post", url: getApiUrl("create_or_update_custom_fields"), data: payload })
            .then(({ data }) => {
                const savedField = (Array.isArray(data?.custom_fields)
                    ? data.custom_fields[0]
                    : data?.custom_fields) || data?.custom_field || data?.field;
                if (!savedField) {
                    return apiAgent({ method: "get", url: "/team/custom_fields", params: { with_counts: true } })
                        .then(({ data: refreshed }) => {
                            const fields = refreshed.custom_fields || [];
                            setCustomFields(fields);
                            const refreshedField = selectedField
                                ? fields.find((field) => field.id === selectedField.id)
                                : fields.find((field) => field.field_name.trim().toLowerCase() === form.field_name.trim().toLowerCase());
                            if (!refreshedField) throw new Error("The custom field was not returned after saving.");
                            forgetCounts(refreshedField.id);
                            toast.success(selectedField ? "Custom field updated" : "Custom field created");
                            resetEditor();
                        });
                }
                setCustomFields((fields) => selectedField
                    ? fields.map((field) => field.id === savedField.id ? { ...savedField, answers_count: field.answers_count } : field)
                    : [...fields, { ...savedField, answers_count: 0 }]);
                forgetCounts(savedField.id);
                toast.success(selectedField ? "Custom field updated" : "Custom field created");
                resetEditor();
            })
            .catch((error) => {
                const message = errorMessage(error, "Could not save the custom field");
                if (/field name|already exists/i.test(message)) setServerFieldError(message);
                else if (/already being processed|processing/i.test(message)) toast.warning(message);
                else toast.error(message);
            })
            .finally(() => setIsLoading((state) => ({ ...state, saveField: false })));
    };

    const openBulkPaste = () => {
        setBulkOptions("");
        setModal("bulkPaste");
    };

    const addBulkOptions = () => {
        if (!bulkSummary.additions.length) return;
        setForm((state) => ({
            ...state,
            choice_options: (() => {
                let additionIndex = 0;
                const filled = state.choice_options.map((option) => {
                    if (option.label.trim() || additionIndex >= bulkSummary.additions.length) return option;
                    const label = bulkSummary.additions[additionIndex];
                    additionIndex += 1;
                    return { ...option, label };
                });
                return [
                    ...filled,
                    ...bulkSummary.additions.slice(additionIndex).map((label) => ({ id: null, label })),
                ];
            })(),
        }));
        setBulkOptions("");
        setModal("fieldForm");
    };

    const requestOptionRemoval = (option, index) => {
        if (!option.id) {
            setForm((state) => ({
                ...state,
                choice_options: state.choice_options.filter((_, optionIndex) => optionIndex !== index),
            }));
            return;
        }
        if (isDirty) {
            toast.warning("Save or discard your other changes before removing an existing option");
            return;
        }
        if (form.choice_options.length <= 2) {
            toast.error("A choice field must keep at least two options");
            return;
        }
        setRemoval({ option, answer_action: "clear", replacement_option_id: "" });
        setModal("removeOption");
    };

    const removeOption = () => {
        if (removal.answer_action === "move" && !removal.replacement_option_id) {
            toast.error("Choose the replacement option");
            return;
        }

        setIsLoading((state) => ({ ...state, removeOption: true }));
        apiAgent({
            method: "post",
            url: `/team/custom_fields/${selectedField.id}/remove_option`,
            data: {
                option_id: removal.option.id,
                answer_action: removal.answer_action,
                replacement_option_id: removal.replacement_option_id || undefined,
            },
        })
            .then(({ data }) => {
                setCustomFields((fields) => fields.map((field) => field.id === selectedField.id ? {
                    ...field,
                    choice_options: (field.choice_options || []).filter((option) => option.id !== removal.option.id),
                    options: (field.options || []).filter((label) => label !== removal.option.label),
                } : field));
                forgetCounts(selectedField.id);
                toast.info(removal.answer_action === "move"
                    ? "Option will be removed and answers moved shortly"
                    : "Option will be removed shortly");
                resetEditor();
            })
            .catch((error) => {
                const message = errorMessage(error, "Could not remove the option");
                if (/already being processed|processing/i.test(message)) toast.warning(message);
                else toast.error(message);
            })
            .finally(() => setIsLoading((state) => ({ ...state, removeOption: false })));
    };

    const updateFilter = (field) => {
        setIsLoading((state) => ({ ...state, [field.id]: true }));
        apiAgent({
            method: "post",
            url: getApiUrl("create_or_update_custom_fields"),
            data: { field_id: field.id, indexing_allowed: !field.indexing_allowed },
        })
            .then(({ data }) => {
                const updated = data.custom_fields[0];
                setCustomFields((fields) => fields.map((item) => item.id === updated.id ? updated : item));
                if (!updated.indexing_allowed) {
                    setCountsByField((state) => {
                        const next = { ...state };
                        delete next[field.id];
                        return next;
                    });
                }
                toast.success(updated.indexing_allowed ? "Filtering enabled" : "Filtering disabled");
                setModal(null);
                setSelectedField(null);
            })
            .catch((error) => toast.error(errorMessage(error, "Failed to update custom field")))
            .finally(() => setIsLoading((state) => ({ ...state, [field.id]: false })));
    };

    const requestFilterUpdate = (field, event) => {
        setSelectedField(field);
        if (!field.indexing_allowed) return updateFilter(field);

        const anchorEl = (event?.currentTarget || event?.target)?.closest?.(".data-fields__filter-control");
        setConfirmAnchorEl(anchorEl || null);
        setConfirmAnchor(anchorEl ? anchorPosition(anchorEl) : null);
        setModal("toggleFilter");
    };

    const openDelete = (field) => {
        setSelectedField(field);
        setDeleteConfirmation("");
        setModal("deleteField");
    };

    const deleteField = () => {
        if (!selectedField) return;
        setIsLoading((state) => ({ ...state, deleteField: true }));
        apiAgent({ method: "post", url: getApiUrl("delete_custom_field"), data: { field_id: selectedField.id } })
            .then(({ data }) => {
                setCustomFields((fields) => fields.filter((field) => field.id !== selectedField.id));
                toast.info("Custom field will be deleted shortly");
                resetEditor();
            })
            .catch((error) => toast.error(errorMessage(error, "Could not delete the custom field")))
            .finally(() => setIsLoading((state) => ({ ...state, deleteField: false })));
    };

    const renderOptionsPreview = (field) => {
        const options = normalizeOptions(field);
        const counts = countsByField[field.id] || {};
        const answerRule = field.field_type === "Check Box"
            ? "multiple answers per prospect"
            : "one answer per prospect";
        const countsLoading = !!isLoading[`counts-${field.id}`];
        return (
            <Popover
                placement="bottom-start"
                showArrow={false}
                customStyle={{ width: "304px", maxWidth: "304px", padding: 0 }}
                tooltipText={() => (
                    <div className="data-fields__options-preview">
                        <div className="data-fields__preview-heading">
                            <div className="body-s-semibold color-text-primary truncate">{field.field_name}</div>
                            <div className="flex items-center gap-s">
                                <span className="support-m color-text-secondary">{field.field_type} · {answerRule}</span>
                                {field.indexing_allowed && countsLoading && <Loader isLoading Size="Small" />}
                            </div>
                        </div>
                        <div className="data-fields__preview-list">
                            {options.map((option) => (
                                <div className="data-fields__preview-row" key={option.id || option.label}>
                                    <span className="data-fields__preview-option">
                                        <ChoiceMarker markerType={CHOICE_MARKER_TYPES[field.field_type]} />
                                        <span className="body-s truncate">{option.label}</span>
                                    </span>
                                    {field.indexing_allowed && !countsLoading && (
                                        <span className="support-m color-text-secondary">{counts[option.id] ?? 0}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                        {canEditCustomField && (
                            <button type="button" className="data-fields__preview-action body-s-semibold" onClick={() => openEdit(field)}>
                                <Icons name="edit" size={16} />
                                Manage options
                            </button>
                        )}
                    </div>
                )}
            >
                <button type="button" className="data-fields__options-trigger body-s-semibold" onClick={() => loadOptionCounts(field)}>
                    {options.length} {options.length === 1 ? "option" : "options"}
                </button>
            </Popover>
        );
    };

    const renderFieldForm = () => {
        const totalAnswers = Object.values(optionCounts).reduce((total, count) => total + count, 0);
        const completeOptions = form.choice_options.filter((option) => option.label.trim());
        const uniqueOptionCount = new Set(completeOptions.map((option) => option.label.trim().toLowerCase())).size;
        const formReady = Boolean(form.field_name.trim() && form.field_type) && (
            !choiceField || !optionsChanged || (completeOptions.length >= 2 && uniqueOptionCount === completeOptions.length)
        );
        const initialForm = initialSnapshot ? JSON.parse(initialSnapshot) : null;
        const originalOptionsById = new Map((selectedField ? normalizeOptions(selectedField) : []).map((option) => [option.id, option]));
        const savedOptionIds = (options) => options.filter((option) => option.id).map((option) => option.id).join(",");
        const optionOrderChanged = Boolean(selectedField && initialForm) &&
            savedOptionIds(initialForm.choice_options) !== savedOptionIds(form.choice_options);
        const footerStatus = selectedField
            ? (isDirty
                ? `Unsaved changes${optionOrderChanged ? " · new order applies on save" : ""}`
                : "No unsaved changes")
            : (!form.field_type
                ? "Pick a type to continue"
                : (choiceField
                    ? (completeOptions.length >= 2
                        ? `${completeOptions.length} options · minimum 2`
                        : "Add at least 2 options to continue")
                    : "Text fields need no further setup"));

        return (
            <Popup
                name={selectedField ? "Edit Custom Field" : "Add Custom Field"}
                subtitle={selectedField
                    ? (selectedField.indexing_allowed
                        ? `${selectedField.field_name} · ${totalAnswers.toLocaleString()} ${totalAnswers === 1 ? "answer" : "answers"}`
                        : selectedField.field_name)
                    : "Captured on every prospect profile. You can rename it later."}
                show={isFieldEditorOpen}
                handleOnClosePopup={requestEditorClose}
                maxWidth={760}
                customClassName="data-fields-dialog data-fields-dialog--editor"
                footerContent={(
                    <div className="data-fields__dialog-footer">
                        <span className="body-s-semibold color-text-secondary">{footerStatus}</span>
                        <div className="data-fields__dialog-actions">
                            <Button Type="Secondary" Size="Medium" onClick={requestEditorClose}>Cancel</Button>
                            <Button
                                Type="Primary"
                                Size="Medium"
                                onClick={saveField}
                                isLoading={!!isLoading.saveField}
                                disabled={!isDirty || !formReady}
                            >
                                {selectedField ? "Save changes" : "Create Field"}
                            </Button>
                        </div>
                    </div>
                )}
            >
                <div className="data-fields__popup-body">
                    <div className="data-fields__field-block">
                        <div className="data-fields__label-row">
                            <span className="body-s-semibold color-text-secondary">Field Name</span>
                            {!selectedField && (
                                <span className="support-m color-text-secondary">{form.field_name.length} / {FIELD_NAME_LIMIT}</span>
                            )}
                        </div>
                        <InputBox
                            Size="Large"
                            customClass="data-fields__field-name-input"
                            value={form.field_name}
                            placeholder="e.g. Chapter"
                            error={fieldNameError}
                            handleOnChange={(event) => {
                                setForm((state) => ({ ...state, field_name: event.target.value.slice(0, FIELD_NAME_LIMIT) }));
                                setServerFieldError(null);
                            }}
                        />
                        <div className={`data-fields__field-helper support-m${fieldNameError ? " color-text-error" : " color-text-secondary"}`}>
                            {fieldNameError || (selectedField
                                ? "Renaming updates the label everywhere. Saved answers are untouched."
                                : "Use a short, plural-free label — it becomes a column and a filter name.")}
                        </div>
                    </div>

                    <div className="data-fields__field-block">
                        <div className="data-fields__label-row">
                            <span className="body-s-semibold color-text-secondary">Field Type</span>
                            {!selectedField && (
                                <span className="data-fields__lock-note support-m color-text-secondary">
                                    <Icons name="lock" size={16} />
                                    Can’t be changed later
                                </span>
                            )}
                        </div>
                        {!form.field_type && !selectedField ? (
                            <div className="data-fields__type-grid">
                                {FIELD_TYPES.map((fieldType) => (
                                    <button
                                        type="button"
                                        className="data-fields__type-card"
                                        key={fieldType}
                                        onClick={() => setFieldType(fieldType)}
                                    >
                                        <span className="data-fields__type-card-title body-m-semibold">
                                            {renderFieldTypeGlyph(fieldType)}
                                            {fieldType}
                                        </span>
                                        <span className="body-s-semibold color-text-secondary">{FIELD_TYPE_PICKER_HELP[fieldType]}</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className={`data-fields__selected-type${selectedField ? " data-fields__selected-type--locked" : ""}`}>
                                {renderFieldTypeGlyph(form.field_type)}
                                <div className="data-fields__selected-type-copy">
                                    <span className="body-m-semibold">{form.field_type}</span>
                                    {!selectedField && <span className="support-m color-text-secondary">{FIELD_TYPE_HELP[form.field_type]}</span>}
                                </div>
                                {selectedField ? (
                                    <span className="data-fields__locked-label support-m color-text-secondary">
                                        <Icons name="lock" size={16} /> Locked
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="data-fields__change-type body-s-semibold"
                                        onClick={() => setForm((state) => ({ ...state, field_type: "", choice_options: [] }))}
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {choiceField && (
                        <div className="data-fields__choice-editor">
                            <div className="data-fields__choice-header">
                                <span className="body-s-semibold color-text-secondary">Options</span>
                                <div className="data-fields__choice-header-actions">
                                    {selectedField && <span className="support-m color-text-secondary">Drag to reorder</span>}
                                    <button type="button" className="data-fields__text-action body-s-semibold" onClick={openBulkPaste}>
                                        <Icons name="copy" size={16} />
                                        Paste a list
                                    </button>
                                </div>
                            </div>
                            {optionSummaryError && <div className="support-m color-text-error">{optionSummaryError}</div>}
                            <div className="data-fields__option-list">
                                {form.choice_options.map((option, index) => {
                                    const original = option.id ? originalOptionsById.get(option.id) : null;
                                    const labelChanged = original && original.label.trim() !== option.label.trim();
                                    return (
                                        <div className="data-fields__option-item" key={option.id || `new-${index}`}>
                                            <div
                                                className={`data-fields__option-row${draggedOptionIndex === index ? " data-fields__option-row--dragging" : ""}`}
                                                draggable
                                                onDragStart={() => setDraggedOptionIndex(index)}
                                                onDragOver={(event) => event.preventDefault()}
                                                onDrop={() => {
                                                    moveOption(draggedOptionIndex, index);
                                                    setDraggedOptionIndex(null);
                                                }}
                                                onDragEnd={() => setDraggedOptionIndex(null)}
                                            >
                                                <IconButton
                                                    IconName="drag"
                                                    Size="ExtraSmall"
                                                    Type="Tertiary"
                                                    ShowHover={false}
                                                    ariaLabel={`Drag ${option.label || `option ${index + 1}`}`}
                                                    customClassName="data-fields__drag-handle"
                                                />
                                                <ChoiceMarker markerType={CHOICE_MARKER_TYPES[form.field_type]} />
                                                <div className="data-fields__option-input">
                                                    <InputBox
                                                        Size="Large"
                                                        customClass="data-fields__option-label-input"
                                                        value={option.label}
                                                        placeholder={index === 0 ? "e.g. Delhi NCR" : index === 1 ? "e.g. Mumbai" : `Option ${index + 1}`}
                                                        error={optionErrors[index]}
                                                        handleOnChange={(event) => updateOption(index, event.target.value)}
                                                    />
                                                </div>
                                                <span className="data-fields__option-actions">
                                                    {selectedField?.indexing_allowed && option.id && (
                                                        <span className="data-fields__option-count support-m color-text-secondary">
                                                            {(optionCounts[option.id] ?? 0).toLocaleString()} answers
                                                        </span>
                                                    )}
                                                    <IconButton
                                                        IconName="delete"
                                                        Size="ExtraSmall"
                                                        Type="DangerTertiary"
                                                        onClick={() => requestOptionRemoval(option, index)}
                                                        disabled={form.choice_options.length <= 2}
                                                        ariaLabel={`Remove ${option.label || `option ${index + 1}`}`}
                                                    />
                                                </span>
                                            </div>
                                            {optionErrors[index] && (
                                                <div className="data-fields__option-note data-fields__option-note--error support-m color-text-error">
                                                    <Icons name="warning_status" size={16} />
                                                    {optionErrors[index]}
                                                </div>
                                            )}
                                            {labelChanged && (
                                                <div className="data-fields__option-note support-m color-text-secondary">
                                                    <Icons name="info" size={16} />
                                                    {selectedField.indexing_allowed
                                                        ? `${(optionCounts[option.id] ?? 0).toLocaleString()} prospects keep this answer — only the label changes.`
                                                        : "Saved answers keep this option — only the label changes."}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <button type="button" className="data-fields__add-option body-m-semibold" onClick={addOption}>
                                <Icons name="plus" size={16} />
                                Add option
                            </button>
                        </div>
                    )}
                </div>
            </Popup>
        );
    };

    const renderBulkPaste = () => (
        <Popup
            name="Paste your options"
            subtitle="One option per line. Spaces are trimmed and duplicates are skipped."
            show={modal === "bulkPaste"}
            handleOnClosePopup={() => setModal("fieldForm")}
            maxWidth={620}
            customClassName="data-fields-dialog data-fields-dialog--bulk"
            footerContent={(
                <div className="data-fields__dialog-footer">
                    <span className="body-s-semibold color-text-secondary">Existing options are kept — this only adds new ones</span>
                    <div className="data-fields__dialog-actions">
                        <Button Type="Secondary" Size="Medium" onClick={() => setModal("fieldForm")}>Back</Button>
                        <Button Type="Primary" Size="Medium" onClick={addBulkOptions} disabled={bulkSummary.additions.length === 0}>
                            Add {bulkSummary.additions.length} option{bulkSummary.additions.length === 1 ? "" : "s"}
                        </Button>
                    </div>
                </div>
            )}
        >
            <div className="data-fields__popup-body">
                <InputArea
                    value={bulkOptions}
                    rows={9}
                    placeholder="One option per line"
                    customClass="data-fields__bulk-input"
                    handleOnChange={(event) => setBulkOptions(event.target.value)}
                />
                <div className="data-fields__bulk-summary support-m">
                    <Icons name="check" size={16} />
                    <span>
                        {bulkSummary.additions.length} option{bulkSummary.additions.length === 1 ? "" : "s"} will be added
                        {bulkSummary.duplicates.length > 0 && (
                            <> · {bulkSummary.duplicates.length} duplicate{bulkSummary.duplicates.length === 1 ? "" : "s"} skipped
                                {bulkSummary.duplicates.length === 1 ? ` (“${bulkSummary.duplicates[0]}”)` : ""}</>
                        )}
                    </span>
                </div>
            </div>
        </Popup>
    );

    const renderRemoveOption = () => {
        const usageCount = optionCounts[removal.option?.id];
        const replacements = form.choice_options.filter((option) => option.id && option.id !== removal.option?.id);
        const countLabel = usageCount === undefined ? "prospects using this option" : `${usageCount.toLocaleString()} prospects`;

        return (
            <Popup
                name={`Remove “${removal.option?.label}” from ${selectedField?.field_name}?`}
                subtitle={usageCount === undefined
                    ? "Choose what happens to answers on unarchived prospects."
                    : `${usageCount.toLocaleString()} prospects picked this option. Choose what happens to their answers.`}
                show={modal === "removeOption"}
                handleOnClosePopup={() => setModal("fieldForm")}
                maxWidth={540}
                customClassName="data-fields-dialog data-fields-dialog--remove"
                primaryAction={{
                    label: "Remove option",
                    Type: "Danger",
                    onClick: removeOption,
                    isLoading: !!isLoading.removeOption,
                    disabled: removal.answer_action === "move" && !removal.replacement_option_id,
                }}
                secondaryAction={{ label: "Cancel", onClick: () => setModal("fieldForm") }}
                showHeaderDivider={false}
            >
                <div className="data-fields__popup-body">
                    <div className="data-fields__removal-options">
                        <div
                            className={`data-fields__removal-card${removal.answer_action === "clear" ? " data-fields__removal-card--selected" : ""}`}
                            onClick={() => setRemoval((state) => ({ ...state, answer_action: "clear", replacement_option_id: "" }))}
                        >
                            <SelectionControl
                                type="radio"
                                name="answer_action"
                                value="clear"
                                checked={removal.answer_action === "clear"}
                                onChange={() => setRemoval((state) => ({ ...state, answer_action: "clear", replacement_option_id: "" }))}
                                label={`Clear the answer for ${countLabel}`}
                            />
                            <span className="support-m color-text-secondary">
                                “{selectedField?.field_name}” becomes empty on those profiles.
                                {selectedField?.indexing_allowed && ` Filters using ${removal.option?.label} are removed.`}
                            </span>
                        </div>
                        <div
                            className={`data-fields__removal-card${removal.answer_action === "move" ? " data-fields__removal-card--selected" : ""}`}
                            onClick={() => setRemoval((state) => ({ ...state, answer_action: "move" }))}
                        >
                            <SelectionControl
                                type="radio"
                                name="answer_action"
                                value="move"
                                checked={removal.answer_action === "move"}
                                onChange={() => setRemoval((state) => ({ ...state, answer_action: "move" }))}
                                label="Move them to another option"
                            />
                            <span className="support-m color-text-secondary">Their answer is replaced with the option you pick below.</span>
                            <SelectBox
                                Size="Large"
                                value={removal.replacement_option_id}
                                placeholder="Choose an option"
                                disabled={removal.answer_action !== "move"}
                                containerClass="data-fields__replacement-select"
                                align="left"
                                options={replacements.map((option) => ({ label: option.label, value: option.id }))}
                                handleOnChange={(replacementOptionId) => setRemoval((state) => ({ ...state, replacement_option_id: replacementOptionId }))}
                            />
                        </div>
                    </div>
                    <div className="data-fields__danger-note body-s-semibold">
                        <Icons name="delete" size={16} />
                        This can’t be undone
                    </div>
                </div>
            </Popup>
        );
    };

    const deletePhrase = selectedField?.field_name?.toUpperCase() || "";
    const isDeleteConfirmed = deleteConfirmation.trim() === deletePhrase;
    const deleteAnswerCount = selectedField?.answers_count || 0;

    return (
        <div className="settings-panel">
            <div className="settings-panel__header data-fields__header">
                <div className="flex-1 min-w-0">
                    <div className="settings-panel__title body-l-bold">Data Fields</div>
                    <div className="settings-panel__description body-s-semibold">
                        Default fields come built in. Add custom fields to capture extra details about your prospects.
                    </div>
                </div>
                <ConditionalRender if={canCreateCustomField}>
                    <div className="flex-shrink-0">
                        <Button
                            Size="Small"
                            Type="Primary"
                            customClassName="whitespace-nowrap"
                            onClick={openCreate}
                        >
                            Add Custom Field
                        </Button>
                    </div>
                </ConditionalRender>
            </div>

            {!isDataLoaded ? <Loader isLoading Size="Medium" /> : (
                <>
                    <div className="settings-panel__section">
                        {customFields.length === 0 && (
                            <EmptyState
                                className="data-fields__empty"
                                size="s"
                                icon={<span className="data-fields__empty-icon"><Icons name="user-attribute" size={24} /></span>}
                                title="No custom fields yet"
                                description="Custom fields capture what matters to your team — chapter, batch year, interests — and become filters across prospects."
                                primaryAction={canCreateCustomField ? { label: "Add Custom Field", onClick: openCreate } : null}
                            />
                        )}

                        {customFields.length > 0 && (
                            <div className="data-fields__table-wrap">
                                <table className="data-fields__table">
                                    <thead>
                                        <tr>
                                            <th>Custom Field</th>
                                            <th>Type</th>
                                            <th>Options</th>
                                            {hasFilterAccess && <th>Filter</th>}
                                            <th aria-label="Actions"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customFields.map((field) => (
                                            <tr key={field.id}>
                                                <td>{field.field_name}</td>
                                                <td>
                                                    <span className="data-fields__field-type-chip">
                                                        {renderFieldTypeGlyph(field.field_type)}
                                                        <span>{field.field_type}</span>
                                                    </span>
                                                </td>
                                                <td>{CHOICE_TYPES.includes(field.field_type) ? renderOptionsPreview(field) : <span>—</span>}</td>
                                                {hasFilterAccess && (
                                                    <td>
                                                        {field.is_indexable ? (
                                                            isLoading[field.id] ? <Loader isLoading Size="Small" /> : (
                                                                <div className="data-fields__filter-control">
                                                                    <SelectionControl
                                                                        type="toggle"
                                                                        checked={field.indexing_allowed}
                                                                        onChange={(event) => requestFilterUpdate(field, event)}
                                                                        className="data-fields__filter-toggle"
                                                                        aria-label={`${field.indexing_allowed ? "Disable" : "Enable"} filtering for ${field.field_name}`}
                                                                    />
                                                                    {modal === "toggleFilter" && selectedField?.id === field.id && confirmAnchor &&
                                                                        ReactDOM.createPortal(
                                                                            <AnConfirmDropdown
                                                                                message={`Stop using “${field.field_name}” as a filter?`}
                                                                                btnLabels={{ submitLabel: "Turn off", cancelLabel: "Keep it on" }}
                                                                                isLoading={!!isLoading[field.id]}
                                                                                onSubmitCallback={() => updateFilter(field)}
                                                                                onCancelCallback={resetEditor}
                                                                                fixedElementStyle={{ zIndex: CONFIRM_Z_INDEX - 1 }}
                                                                                dropdownStyle={{
                                                                                    position: "fixed",
                                                                                    top: confirmAnchor.top,
                                                                                    left: confirmAnchor.left,
                                                                                    right: "auto",
                                                                                    width: `${CONFIRM_WIDTH}px`,
                                                                                    zIndex: CONFIRM_Z_INDEX,
                                                                                }}
                                                                            />, document.body)}
                                                                </div>
                                                            )
                                                        ) : <span className="data-fields__filter-na body-s">Not filterable</span>}
                                                    </td>
                                                )}
                                                <td className="data-fields__table-actions">
                                                    <ConditionalRender if={canEditCustomField || canDeleteCustomField}>
                                                        <ClickableDropdown>
                                                            <IconButton IconName="vertical-ellipsis" Size="ExtraSmall" Type="Tertiary" ariaLabel={`Actions for ${field.field_name}`} />
                                                            <DropdownMenu usePortal align="right" customClasses="data-fields__actions-menu">
                                                                {canEditCustomField && (
                                                                    <DropdownItem IconName="edit" onClick={() => openEdit(field)}>Rename field</DropdownItem>
                                                                )}
                                                                {canEditCustomField && CHOICE_TYPES.includes(field.field_type) && (
                                                                    <DropdownItem IconName="list" onClick={() => openEdit(field)}>
                                                                        <span>Manage options</span>
                                                                        <span className="data-fields__actions-count">{normalizeOptions(field).length}</span>
                                                                    </DropdownItem>
                                                                )}
                                                                {canDeleteCustomField && (
                                                                    <DropdownItem IconName="delete" customClasses="dropdown-item-danger" onClick={() => openDelete(field)}>Delete field</DropdownItem>
                                                                )}
                                                            </DropdownMenu>
                                                        </ClickableDropdown>
                                                    </ConditionalRender>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="settings-panel__section">
                        <div className="data-fields__default-wrap">
                            <div className="data-fields__default-header body-s-semibold">Default Field</div>
                            {allDefaultFields.map((field) => (
                                <div key={field} className="data-fields__default-row body-s-semibold">{field}</div>
                            ))}
                            <div className="data-fields__default-row body-s-semibold">
                                <span>Assigned Team Member</span>
                                <ConditionalRender if={checkUserAccess("add_team_member")}>
                                    <button className="data-fields__link body-s-semibold" onClick={() => props.switchTab?.("people")}>Add Team Members</button>
                                </ConditionalRender>
                            </div>
                            <div className="data-fields__default-row body-s-semibold">
                                <span>Tags</span>
                                <button className="data-fields__link body-s-semibold" onClick={() => handleNavigate("/manage/tags")}>Go to Tags Page</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {isFieldEditorOpen && renderFieldForm()}
            {modal === "bulkPaste" && renderBulkPaste()}
            {modal === "removeOption" && renderRemoveOption()}

            <Popup
                name="Discard your changes?"
                subtitle="Closing now discards your renamed, added, or reordered options."
                show={modal === "discardChanges"}
                handleOnClosePopup={() => setModal("fieldForm")}
                maxWidth={468}
                customClassName="data-fields-dialog data-fields-dialog--confirm"
                primaryAction={{ label: "Discard changes", Type: "Danger", onClick: resetEditor }}
                secondaryAction={{ label: "Keep editing", onClick: () => setModal("fieldForm") }}
                showHeaderDivider={false}
            >
                <div />
            </Popup>

            <Popup
                name={`Delete “${selectedField?.field_name || "custom field"}”?`}
                titleIcon="delete"
                subtitle="This removes the field and every answer stored against it. It cannot be undone."
                show={modal === "deleteField"}
                handleOnClosePopup={resetEditor}
                maxWidth={560}
                customClassName="data-fields-dialog data-fields-dialog--delete"
                primaryAction={{ label: "Delete field", Type: "Danger", onClick: deleteField, isLoading: !!isLoading.deleteField, disabled: !isDeleteConfirmed }}
                secondaryAction={{ label: "Cancel", onClick: resetEditor }}
                showHeaderDivider={false}
            >
                <div className="data-fields__popup-body">
                    {deleteAnswerCount > 0 && (
                        <div className="data-fields__delete-impact body-s-semibold">
                            <Icons name="close" size={16} />
                            {`${deleteAnswerCount.toLocaleString()} prospect answer${deleteAnswerCount === 1 ? "" : "s"} will be deleted`}
                        </div>
                    )}
                    <InputBox
                        label={`Type ${deletePhrase} to confirm`}
                        showLabel
                        Size="Large"
                        customClass="data-fields__delete-confirm-input"
                        value={deleteConfirmation}
                        placeholder={deletePhrase}
                        handleOnChange={(event) => setDeleteConfirmation(event.target.value)}
                    />
                </div>
            </Popup>
        </div>
    );
};

export default ManageDataFields;
